<template>
  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <!-- Hero：avatar + name + 徽章（精簡；save/reset/preview/help 全由外層 PageHeader actions slot 注入） -->
    <div class="px-4 py-3 flex items-center gap-3 border-b border-slate-200">
      <div class="w-9 h-9 rounded-lg bg-indigo-500 text-white text-sm font-bold flex items-center justify-center shrink-0">
        {{ initialOf(user) }}
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-1.5 flex-wrap">
          <h2 class="text-sm font-semibold text-slate-800 truncate">{{ displayName(user) }}</h2>
          <span class="text-[11px] font-mono text-slate-400">@{{ user && user.UserName }}</span>
          <span
            v-if="primaryDepartmentName"
            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-600"
          >
            <v-icon size="10" color="#475569" aria-hidden="true">mdi-domain</v-icon>
            {{ primaryDepartmentName }}
          </span>
          <span
            v-for="gname in userGroupNames"
            :key="gname"
            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-600"
          >
            <v-icon size="10" color="#475569" aria-hidden="true">mdi-account-group</v-icon>
            {{ gname }}
          </span>
          <span
            v-if="!primaryDepartmentName && userGroupNames.length === 0"
            class="text-[11px] text-slate-400 italic"
          >
            {{ $t('permission_not_assigned_yet') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Matrix Table -->
    <div v-if="!loading && visibleNodes.length" class="overflow-auto max-h-[calc(100vh-14rem)]">
      <table class="min-w-full border-collapse">
        <thead class="sticky top-0 z-10 bg-slate-50">
          <tr class="border-b border-slate-200">
            <th class="sticky left-0 z-20 bg-slate-50 text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-64">
              {{ $t('permission_col_resource') }}
            </th>
            <th
              v-for="a in ACTIONS"
              :key="a"
              class="text-center px-2 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider min-w-[150px]"
            >
              {{ $t('permission_action_' + a) }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100/60">
          <tr
            v-for="node in visibleNodes"
            :key="node.resource.key"
            class="hover:bg-indigo-50/60 transition-colors duration-200 group"
          >
            <td class="sticky left-0 z-10 bg-white group-hover:bg-indigo-50/60 px-4 py-3 align-top transition-colors duration-200">
              <div class="flex items-start gap-1.5" :style="{ paddingLeft: (node.depth * 16) + 'px' }">
                <button
                  v-if="!node.isLeaf"
                  type="button"
                  class="flex items-center justify-center w-4 h-4 text-slate-400 hover:text-slate-700 cursor-pointer shrink-0 mt-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 rounded"
                  :aria-label="isCollapsed(node.resource.key) ? $t('permission_expand_aria') : $t('permission_collapse_aria')"
                  :aria-expanded="!isCollapsed(node.resource.key)"
                  @click="toggleCollapse(node.resource.key)"
                >
                  <v-icon size="14" color="currentColor">
                    {{ isCollapsed(node.resource.key) ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
                  </v-icon>
                </button>
                <span v-else class="w-4 shrink-0" aria-hidden="true"></span>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <span class="text-sm font-medium text-slate-800 truncate">{{ node.resource.displayName }}</span>
                    <span
                      class="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[10px] font-semibold border shrink-0"
                      :class="kindMeta(node.resource).chipClass"
                    >
                      <v-icon size="10" :color="kindIconColor(kindOf(node.resource))">
                        {{ kindMeta(node.resource).icon }}
                      </v-icon>
                      {{ $t(kindMeta(node.resource).labelKey) }}
                    </span>
                    <span
                      v-if="node.resource.isPreview"
                      class="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                    >
                      {{ $t('permission_preview_badge') }}
                    </span>
                    <v-icon
                      v-if="node.resource.description"
                      size="12"
                      color="#94a3b8"
                      class="cursor-help shrink-0"
                      :title="node.resource.description"
                    >
                      mdi-information-outline
                    </v-icon>
                  </div>
                  <div class="text-[11px] font-mono text-slate-400 mt-0.5 truncate">{{ node.resource.key }}</div>
                </div>
              </div>
            </td>
            <td
              v-for="a in ACTIONS"
              :key="a"
              class="px-1.5 py-2 text-center align-middle"
            >
              <template v-if="supportsAction(node.resource, a)">
                <div class="inline-flex flex-col items-stretch gap-1 w-full">
                  <permission-cell-toggle
                    :mode="modeOf(node.resource.key, a)"
                    :inherited-value="inheritedValueOf(node.resource.key, a)"
                    :label="cellLabel(node.resource.key, a)"
                    :sub-text="cellSubText(node.resource.key, a)"
                    :disabled="node.resource.isPreview || isLegacyCell(node.resource.key, a) || node.resource.isInCatalog === false"
                    @change="mode => onChangeMode(node, a, mode)"
                  />
                  <permission-scope-chip
                    v-if="node.resource.supportsScope && (node.resource.isPreview || modeOf(node.resource.key, a) === OVERRIDE_MODE.GRANT)"
                    :scope="cellScope(node.resource.key, a)"
                    :disabled="node.resource.isPreview || isLegacyCell(node.resource.key, a) || node.resource.isInCatalog === false"
                    @change="s => onChangeScope(node, a, s)"
                  />
                </div>
              </template>
              <!-- 不支援此動作：輕量佔位避免「—」破格、保留 row 視覺對齊 -->
              <div v-else class="rounded-lg bg-slate-50/60 border border-dashed border-slate-200 text-[10px] text-slate-300 py-3 px-2" :title="$t('permission_cell_na')">
                {{ $t('permission_cell_na') }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- hideLegacy 把資源全篩掉時的 empty state，避免誤以為資源不見 -->
    <div v-else-if="!loading" class="px-4 py-10 text-center text-sm text-slate-400">
      {{ $t('permission_legacy_all_hidden') }}
    </div>

    <!-- Loading skeleton（對應 1 寬欄 + 6 窄欄的真實版型，減少 CLS 感）-->
    <div v-else class="p-4 space-y-3">
      <div v-for="i in 4" :key="i" class="animate-pulse flex gap-2">
        <div class="h-16 bg-slate-100 rounded-lg w-64 shrink-0"></div>
        <div v-for="j in 6" :key="j" class="h-16 bg-slate-50 rounded-lg flex-1 min-w-0"></div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ACTIONS,
  SCOPE,
  OVERRIDE_MODE,
  displayName,
  initialOf,
  supportsAction,
  kindOf,
  kindMeta,
  kindIconColor
} from '@/js/permission-helpers'
import permissionTreeMixin from '@/js/permission-tree-mixin'
import PermissionCellToggle from './PermissionCellToggle.vue'
import PermissionScopeChip from './PermissionScopeChip.vue'

export default {
  name: 'UserPermissionMatrix',
  components: { PermissionCellToggle, PermissionScopeChip },
  mixins: [permissionTreeMixin],
  props: {
    user: { type: Object, required: true },
    effectiveByCell: { type: Object, default: () => ({}) },
    overrides: { type: Object, default: () => ({}) },
    primaryDepartmentName: { type: String, default: '' },
    userGroupNames: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  data () {
    return { ACTIONS, OVERRIDE_MODE }
  },
  methods: {
    displayName,
    initialOf,
    supportsAction,
    kindOf,
    kindMeta,
    kindIconColor,
    onChangeMode (node, action, mode) {
      // true-orphan（DB 殘留、catalog 外）反灰全鎖：前端擋 draft，後端 catalog gate 為後盾
      if (node.resource.isPreview || node.resource.isInCatalog === false) return
      this.$emit('change-mode', { resource: node.resource, action, mode })
      for (const k of this.descendantKeysCached(node.resource.key)) {
        const r = this.resourceByKey.get(k)
        if (r && !r.isPreview && r.isInCatalog !== false && supportsAction(r, action)) {
          this.$emit('change-mode', { resource: r, action, mode })
        }
      }
    },
    modeOf (resourceKey, action) {
      const ov = this.overrides[resourceKey] && this.overrides[resourceKey][action]
      return ov ? ov.mode : OVERRIDE_MODE.INHERIT
    },
    cellScope (resourceKey, action) {
      const ov = this.overrides[resourceKey] && this.overrides[resourceKey][action]
      if (ov && ov.scope !== null && ov.scope !== undefined) return Number(ov.scope)
      const eff = this.effectiveByCell[`${resourceKey}:${action}`]
      if (eff && eff.scope !== null && eff.scope !== undefined) return Number(eff.scope)
      return SCOPE.ALL
    },
    onChangeScope (node, action, scope) {
      if (node.resource.isPreview || node.resource.isInCatalog === false) return
      this.$emit('change-scope', { resource: node.resource, action, scope })
    },
    inheritedValueOf (resourceKey, action) {
      const e = this.effectiveByCell[`${resourceKey}:${action}`]
      return !!(e && e.inheritedValue)
    },
    // Legacy 軌的 cell 不應被個人覆寫（後端 PermissionManagementService 已擋），前端同步鎖定 UI
    isLegacyCell (resourceKey, action) {
      const e = this.effectiveByCell[`${resourceKey}:${action}`]
      return !!(e && e.isLegacy)
    },
    cellLabel (resourceKey, action) {
      const mode = this.modeOf(resourceKey, action)
      if (mode === OVERRIDE_MODE.GRANT) return this.$t('permission_cell_label_allow')
      if (mode === OVERRIDE_MODE.REVOKE) return this.$t('permission_cell_label_deny')
      return this.inheritedValueOf(resourceKey, action)
        ? this.$t('permission_cell_label_allow')
        : this.$t('permission_cell_label_deny')
    },
    cellSubText (resourceKey, action) {
      const e = this.effectiveByCell[`${resourceKey}:${action}`]
      if (!e) return ''
      // Preview mode：單純示範資料，直接回 read-only 提示
      const resource = this.resourceByKey.get(resourceKey)
      if (resource && resource.isPreview) return this.$t('permission_preview_readonly')
      // Legacy 軌：inheritedValue / sources 不可信，顯示「由舊系統管理」
      if (e.isLegacy) return this.$t('permission_cell_inherited_legacy')
      const mode = this.modeOf(resourceKey, action)
      const sources = (e.sources || []).join('、')
      if (mode === OVERRIDE_MODE.GRANT) {
        return e.inheritedValue
          ? this.$t('permission_cell_grant_with_source', { source: sources })
          : this.$t('permission_cell_grant_no_source')
      }
      if (mode === OVERRIDE_MODE.REVOKE) {
        return e.inheritedValue
          ? this.$t('permission_cell_revoke_with_source', { source: sources })
          : this.$t('permission_cell_revoke_no_source')
      }
      if (e.inheritedValue) {
        if (sources) return this.$t('permission_cell_inherited_from', { source: sources })
        return this.$t('permission_cell_inherited_default')
      }
      return this.$t('permission_cell_none')
    }
  }
}
</script>
